import { YofiTelemetry } from '@yofi-ai/telemetry-web-sdk';
import { Workflows } from '../../tracking/tracking-constants';
import { getCurrentDateTimeAsISO } from '../datetime-service';
import LogService from '../log-service';
import { isImpersonating, isInJestRunnerMode } from './analytics-utility';
import { isInTestMode } from '../../global/test-mode/is-in-test-mode'; // testim-related stuff
import CookieService from '../cookie-service';

const YofiPublicToken = window.SS_GLOBALS.frontEndConfig.yofiPublicToken;

// A small convention is being developed as the marketing site's salt is 'www.shipstation.com';
const V3AppJourneyIdSalt = 'ship.shipstation.com';

/**
 * The "Marketing Site" (www.shipstation.com) initializes a Yofi Journey when users navigate there.
 * In order for Yofi to "stitch" together user activity across domains/apps,
 * we need to forward the initializing journeyId so that downstream domains/apps can use it during initialization.
 */
const YofiMarketingSiteCookie = 'yfai_group_id';
const AuthPagesCookie = 'yofi-at';

const DEVELOPMENT_ENVIRONMENT = 'dev';
const environment = window.SS_GLOBALS.environment || DEVELOPMENT_ENVIRONMENT;
const isInDevelopmentMode = environment === DEVELOPMENT_ENVIRONMENT;

const shouldIgnore = () => {
  return (
    isInDevelopmentMode || // dev
    Boolean(isImpersonating()) || // mgmt drop-in
    isInTestMode() || // testim
    isInJestRunnerMode() // unit tests
  );
};

const FifteenMinutes = 900 * 1000;
const SessionDuration = FifteenMinutes;

const shouldTerminateAllPreviousSessions = true;

const WorkflowsYofiShouldCareAbout = {
  [Workflows.BILLING_INFO_FLOW]: true,
  [Workflows.BULK_LABEL_PURCHASE_FLOW]: true,
  [Workflows.LABEL_PURCHASE_FLOW]: true,
  [Workflows.ORDER_IMPORT_FLOW]: true,
  [Workflows.WALLET_REGISTRATION_FLOW]: true,
};

const WorkflowsForCustomEvents = {
  [Workflows.BULK_LABEL_PURCHASE_FLOW]: true,
  [Workflows.LABEL_PURCHASE_FLOW]: true,
};

class Yofi {
  #isInitialized = false;

  #session = null;

  #shouldIgnore = shouldIgnore();

  #journeyData = {};

  initialize(initialLabels) {
    if (this.#shouldIgnore) return;

    const marketingSiteAccessToken = this.readAndRemoveAccessTokenCookie(YofiMarketingSiteCookie);
    const authPagesAccessToken = this.readAndRemoveAccessTokenCookie(AuthPagesCookie);
    const accessToken = authPagesAccessToken || marketingSiteAccessToken;

    try {
      YofiTelemetry.initialize({
        publicToken: YofiPublicToken,
        journeyIdSalt: V3AppJourneyIdSalt,
        accessToken,
      });

      this.#isInitialized = true;
      this.#journeyData = initialLabels;
    } catch (e) {
      LogService.logInfo(`Error initializing Yofi: ${e}`);
    }
  }

  /**
   * @param {Record<string, string>} initialLabels
   * @returns {void}
   */
  startSession(initialLabels) {
    if (this.#shouldIgnore) return;

    if (!this.#isInitialized) {
      this.initialize(initialLabels);
    }

    try {
      const session = YofiTelemetry.startSession(
        {
          duration: SessionDuration,
          labels: initialLabels,
          networkTelemetryConfig: {
            ip: true,
          },
        },
        shouldTerminateAllPreviousSessions,
      );

      this.#session = session;
    } catch (e) {
      LogService.logInfo(`Error starting Yofi session: ${e}`);
    }
  }

  /**
   * Read and clear a cookie that contains an access token set by an upstream *.shipstation.com app (Marketing Site, Auth pages, etc)
   * @param {string} cookieKey
   * @returns {string|undefined}
   */
  readAndRemoveAccessTokenCookie(cookieKey) {
    try {
      const accessToken = CookieService.getCookieValue(cookieKey);
      if (accessToken) {
        CookieService.removeCookie(cookieKey);
      }

      return accessToken;
    } catch (e) {
      LogService.logInfo(`Error fetching Yofi access token: ${e}`);
    }
  }

  handleLogout() {
    if (this.#shouldIgnore) return;

    try {
      this.#session?.stopSession();
      this.#session = null;
    } catch (e) {
      LogService.logInfo(`Error stopping Yofi session: ${e}`);
    }
  }

  /**
   * make sure there's an active session before calling methods on it
   * @returns {void|Error}
   */
  verifyActiveSession() {
    if (this.#shouldIgnore) return;

    try {
      const isActive = this.#session?.inProgress();
      if (!isActive) {
        LogService.logInfo('Starting new Yofi session after previous one expired.');
        this.startSession(this.#journeyData);
      }
    } catch (e) {
      LogService.logInfo(`Error verifying active Yofi session: ${e}`);
    }
  }

  async addLabels(labels) {
    if (this.#shouldIgnore) return;

    try {
      this.verifyActiveSession();
      await this.#session?.addLabels(labels);
    } catch (e) {
      LogService.logInfo(`Error during adding labels to Yofi session: ${e}`);
    }
  }

  async addCustomEvent(event) {
    if (this.#shouldIgnore) return;

    try {
      this.verifyActiveSession();

      /**
       * @todo figure out what useful metadata would be for these events
       *
       * Adds a timestamped custom event to the bundle of
       * an ongoing session.
       * @param eventName (max length 50 characters) a name used
       * to track custom events. E.g. 'TargetElementCreated'
       * @param eventKey (max length 50 characters) a reference key used
       * to ease lookup of specific events. E.g. the input id for a
       * 'TargetElementCreated' event.
       * @param properties (key max length 50 characters, value max length 50 characters)
       * a list of custom properties that correspond with the custom event. E.g.
       * `{ 'DataType':'FirstName' }`
       */
      await this.#session?.addCustomEvent(event);
    } catch (e) {
      LogService.logInfo(`Error during adding custom event to Yofi session: ${e}`);
    }
  }

  /**
   * Attaches metadata to the running Yofi `session` so
   * Incorporare this method into any functions that handle user actions deemed worthy of training Yofi's fraud detection on critical path (label purchase) related workflows.
   * @param {string} workflow workflow constant declared in `ss-ui-client/src/tracking/tracking-constants.js`
   * @param {string} segmentEvent event string that honestly could be declared anywhere
   * @returns {void}
   */
  applySessionMetadataForWorkflow(workflow, segmentEvent) {
    if (this.#shouldIgnore) return;

    try {
      // get label for event
      const shouldApplyLabel = WorkflowsYofiShouldCareAbout[workflow];
      if (!shouldApplyLabel) return;

      // convert event name to label name
      const label = segmentEvent.toLowerCase();
      this.addLabels({ [label]: getCurrentDateTimeAsISO() });

      // it's stull unclear how they use this but we'll find out after sending them data
      const shouldAddCustomEvent = WorkflowsForCustomEvents[workflow];
      if (shouldAddCustomEvent) {
        this.addCustomEvent(segmentEvent);
      }
    } catch (e) {
      LogService.logInfo(`Error applying Yofi labels for ${workflow}: ${e}`);
    }
  }
}

const singleton = new Yofi();
export default singleton;
